// ============================================================
// Form Builder Page — Full Builder with API Persistence
// ============================================================

import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useState, useEffect } from 'react';
import {
    Plus, GripVertical, Trash2, Eye, Save, Settings,
    Copy, ExternalLink, Share2, ChevronDown, ChevronUp,
    X, Check, RotateCcw, Clock, Globe
} from 'lucide-react';
import toast from 'react-hot-toast';

const FIELD_TYPES = [
    { type: 'text', label: 'Short Text', icon: '📝', group: 'basic' },
    { type: 'textarea', label: 'Long Text', icon: '📄', group: 'basic' },
    { type: 'email', label: 'Email', icon: '✉️', group: 'basic' },
    { type: 'phone', label: 'Phone', icon: '📱', group: 'basic' },
    { type: 'number', label: 'Number', icon: '🔢', group: 'basic' },
    { type: 'date', label: 'Date', icon: '📅', group: 'basic' },
    { type: 'select', label: 'Dropdown', icon: '📋', group: 'choice' },
    { type: 'radio', label: 'Radio Group', icon: '🔘', group: 'choice' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️', group: 'choice' },
    { type: 'file', label: 'File Upload', icon: '📎', group: 'advanced' },
    { type: 'table', label: 'Table Grid', icon: '📊', group: 'advanced' },
    { type: 'repeatable', label: 'Repeatable Section', icon: '🔄', group: 'advanced' },
];

const DEFAULT_UNIVERSAL_FORM_SCHEMA = {
    sections: [
        {
            id: "1",
            title: "Personal Information",
            description: "Basic applicant details (Built-in)",
            fields: [
                { id: "f1", type: "text", label: "Full Name", required: true, placeholder: "Enter your full name" },
                { id: "f2", type: "email", label: "Email Address", required: true, placeholder: "you@example.com" },
                { id: "f3", type: "phone", label: "Phone Number", required: false, placeholder: "+91 9876543210" }
            ]
        },
        {
            id: "2",
            title: "Address",
            description: "Present and permanent address details",
            fields: [
                { id: "f4", type: "text", label: "Present Address (Local)", required: false, placeholder: "Current residential address" },
                { id: "f5", type: "text", label: "Permanent Address (Home)", required: false, placeholder: "Permanent home address" }
            ]
        },
        {
            id: "3",
            title: "Date of Birth",
            description: "DOB and age details",
            fields: [
                { id: "f6", type: "date", label: "Date of Birth (DOB)", required: true },
                { id: "f7", type: "number", label: "Age", required: true, placeholder: "Your age in years" }
            ]
        },
        {
            id: "4",
            title: "Father's / Guardian's Details",
            description: "Parent or Guardian information",
            fields: [
                { id: "f8", type: "text", label: "Father's / Guardian's Name", required: true, placeholder: "Father's or guardian's full name" },
                { id: "f9", type: "text", label: "His or Her Occupation", required: true, placeholder: "Their occupation" },
                { id: "f10", type: "text", label: "If Guardian, then relation with them", required: false, placeholder: "Relation with guardian (if applicable)" }
            ]
        },
        {
            id: "5",
            title: "Family Details",
            description: "Marital and children information",
            fields: [
                { id: "f11", type: "radio", label: "Status", required: true, options: ["Single", "Married"] },
                { id: "f12", type: "date", label: "Spouse DOB (for medical policy)", required: false },
                { id: "f13", type: "select", label: "Number of Children", required: false, options: ["0", "1", "2", "3", "4", "5"] },
                { id: "f14", type: "text", label: "Age of Children (Male and Female)", required: false, placeholder: "e.g. 5, 8" },
                { id: "f15", type: "text", label: "DOB of children (for medical policy)", required: false, placeholder: "e.g. DD/MM/YYYY" }
            ]
        },
        {
            id: "6",
            title: "Academic / Technical Qualifications",
            description: "Details of examinations passed",
            fields: [
                {
                    id: "f16",
                    type: "table",
                    label: "Qualifications",
                    required: true,
                    columns: ["Examination Passed", "Board/University", "Year in which Passed", "Division/Marks Obtained", "% Marks"],
                    minRows: 2,
                    maxRows: 10
                }
            ]
        },
        {
            id: "7",
            title: "Extra-Curricular Activities",
            description: "Hobbies, sports, and other activities",
            fields: [
                { id: "f17", type: "textarea", label: "Extra-Curricular Activities", required: false, placeholder: "Describe your extra-curricular activities" }
            ]
        },
        {
            id: "8",
            title: "Experience & CTC Break Up",
            description: "Employment history and compensation details",
            fields: [
                {
                    id: "f18",
                    type: "table",
                    label: "Experience (In Chronological Order)",
                    required: false,
                    columns: ["Employer’s Name", "Period (From - To)", "Post Held", "Nature of Duty", "Salary (Starting - Last)", "Reason of Leaving"],
                    minRows: 1,
                    maxRows: 10
                },
                {
                    id: "f19",
                    type: "table",
                    label: "CTC Break-up",
                    required: false,
                    columns: ["CTC", "Gross", "In Hand", "Fixed", "Variable", "Cash", "Reimbursement"],
                    minRows: 1,
                    maxRows: 5
                }
            ]
        },
        {
            id: "9",
            title: "Background Details",
            description: "Declarations regarding background and past employment",
            fields: [
                { id: "f20", type: "radio", label: "Have you ever been removed from your job?", required: false, options: ["Yes", "No"] },
                { id: "f21", type: "text", label: "If so, give reasons", required: false, placeholder: "Reasons for job removal" },
                { id: "f22", type: "radio", label: "Do you have any criminal background?", required: false, options: ["Yes", "No"] },
                { id: "f23", type: "textarea", label: "If yes, then explain", required: false, placeholder: "Details of criminal background" }
            ]
        },
        {
            id: "10",
            title: "References",
            description: "Referral information",
            fields: [
                { id: "f24", type: "text", label: "Who referred you for the job?", required: false, placeholder: "Name of referral" }
            ]
        },
        {
            id: "11",
            title: "Professional References",
            description: "Professional contacts for verification",
            fields: [
                {
                    id: "f25",
                    type: "table",
                    label: "Professional References",
                    required: false,
                    columns: ["Name", "Designation", "Department", "Company Name", "Contact No."],
                    minRows: 1,
                    maxRows: 5
                }
            ]
        },
        {
            id: "12",
            title: "Declaration & Disclaimer",
            description: "Please read and agree to the terms below",
            fields: [
                {
                    id: "f26",
                    type: "checkbox",
                    label: "Consent",
                    required: true,
                    options: ["Agree"],
                    description: "I hereby declare that: A.) My present residential and permanent (Home) Addresses as given above are correct to the best of my knowledge. In future, if there is any change, I will intimate in writing, otherwise both these addresses will be considered to be as correct and any letter addresses will be considered as delivered. B.) I am not suffering from any medical .If Yes specify. C.) I agree to be on probation for a period of three months, which is liable to be extended, if so desired. My services are liable to be terminated at any time with preferred notice period, during or after the expiry of the probation period. D.) If required, I will arrange to submit true copies of my education and employment certificates. I, hereby solemnly declare and certify that the information given above is true to the best of my knowledge and that if at any time, any of it is found incorrect, then the same will be render me liable for instant dismissal."
                },
                { id: "f27", type: "text", label: "Medical Sufferings explanation field", required: false, placeholder: "If any medical sufferings, specify here" }
            ]
        }
    ],
    settings: {}
};

// ── Main Component ───────────────────────────────────────────

export function FormBuilderPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [sections, setSections] = useState<any[]>([]);
    const [activeField, setActiveField] = useState<{ sectionId: string; fieldId: string } | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    // Fetch form data
    const { data: formData, isLoading } = useQuery({
        queryKey: ['form', id],
        queryFn: () => api.get(`/forms/${id}`),
    });

    const { data: versionsData } = useQuery({
        queryKey: ['form-versions', id],
        queryFn: () => api.get(`/forms/${id}/versions`),
        enabled: showVersions,
    });

    // Load form schema into state
    useEffect(() => {
        if (formData?.data?.currentVersion?.schema?.sections) {
            setSections(formData.data.currentVersion.schema.sections);
            setHasChanges(false);
        } else if (formData?.data && !formData.data.currentVersion?.schema?.sections) {
            // New form with no sections - load Universal Form schema as default
            setSections(DEFAULT_UNIVERSAL_FORM_SCHEMA.sections);
            setHasChanges(true);
        }
    }, [formData]);

    const form = formData?.data;
    const versions = versionsData?.data || [];

    // Save as new version
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post(`/forms/${id}/versions`, {
                schema: { sections, settings: {} },
            });
            queryClient.invalidateQueries({ queryKey: ['form', id] });
            queryClient.invalidateQueries({ queryKey: ['form-versions', id] });
            setHasChanges(false);
            toast.success('Form version saved!');
        } catch (err: any) {
            toast.error(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    // Publish
    const handlePublish = async () => {
        try {
            // Save first if changes
            if (hasChanges) {
                await api.post(`/forms/${id}/versions`, {
                    schema: { sections, settings: {} },
                });
            }
            const result = await api.post(`/forms/${id}/publish`);
            queryClient.invalidateQueries({ queryKey: ['form', id] });
            queryClient.invalidateQueries({ queryKey: ['forms'] });
            const url = result.data.publicUrl;
            navigator.clipboard.writeText(url);
            toast.success('Form published! Link copied to clipboard');
        } catch (err: any) {
            toast.error(err.message || 'Publish failed');
        }
    };

    // Section operations
    const addSection = () => {
        setSections([...sections, {
            id: String(Date.now()), title: 'New Section', description: '', fields: [],
        }]);
        setHasChanges(true);
    };

    const removeSection = (index: number) => {
        setSections(sections.filter((_, i) => i !== index));
        setHasChanges(true);
    };

    const updateSection = (index: number, updates: any) => {
        const updated = [...sections];
        updated[index] = { ...updated[index], ...updates };
        setSections(updated);
        setHasChanges(true);
    };

    // Field operations
    const addField = (sectionId: string, fieldType: string) => {
        const ft = FIELD_TYPES.find(f => f.type === fieldType);
        const newField: any = {
            id: String(Date.now()),
            type: fieldType,
            label: ft?.label || 'Field',
            required: false,
            placeholder: '',
        };
        // Add default config for specific types
        if (fieldType === 'select' || fieldType === 'radio') {
            newField.options = ['Option 1', 'Option 2'];
        }
        if (fieldType === 'checkbox') {
            newField.options = ['Option 1', 'Option 2'];
        }
        if (fieldType === 'table') {
            newField.columns = ['Column 1', 'Column 2', 'Column 3'];
            newField.minRows = 1;
            newField.maxRows = 5;
        }
        if (fieldType === 'repeatable') {
            newField.templateFields = [
                { id: `${Date.now()}-1`, type: 'text', label: 'Entry', required: false },
            ];
            newField.minEntries = 1;
            newField.maxEntries = 10;
        }
        if (fieldType === 'file') {
            newField.acceptedTypes = '.pdf,.doc,.docx,.png,.jpg';
            newField.maxSizeMB = 10;
        }

        setSections(sections.map(s => {
            if (s.id === sectionId) {
                return { ...s, fields: [...s.fields, newField] };
            }
            return s;
        }));
        setActiveField({ sectionId, fieldId: newField.id });
        setHasChanges(true);
    };

    const removeField = (sectionId: string, fieldId: string) => {
        setSections(sections.map(s => {
            if (s.id === sectionId) {
                return { ...s, fields: s.fields.filter((f: any) => f.id !== fieldId) };
            }
            return s;
        }));
        if (activeField?.fieldId === fieldId) setActiveField(null);
        setHasChanges(true);
    };

    const updateField = (sectionId: string, fieldId: string, updates: any) => {
        setSections(sections.map(s => {
            if (s.id === sectionId) {
                return {
                    ...s,
                    fields: s.fields.map((f: any) => f.id === fieldId ? { ...f, ...updates } : f),
                };
            }
            return s;
        }));
        setHasChanges(true);
    };

    const moveField = (sectionId: string, fromIdx: number, direction: 'up' | 'down') => {
        const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
        setSections(sections.map(s => {
            if (s.id === sectionId) {
                if (toIdx < 0 || toIdx >= s.fields.length) return s;
                const fields = [...s.fields];
                [fields[fromIdx], fields[toIdx]] = [fields[toIdx], fields[fromIdx]];
                return { ...s, fields };
            }
            return s;
        }));
        setHasChanges(true);
    };

    // Get active field data
    const getActiveFieldData = () => {
        if (!activeField) return null;
        const section = sections.find(s => s.id === activeField.sectionId);
        return section?.fields.find((f: any) => f.id === activeField.fieldId);
    };
    const activeFieldData = getActiveFieldData();

    if (isLoading) return <div className="loading-page"><div className="loading-spinner" /></div>;

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div className="page-header">
                <div>
                    <Link to="/jobs" style={{ color: '#6b7280', fontSize: '14px' }}>← Back</Link>
                    <h1 className="mt-2">{form?.name || 'Form Builder'}</h1>
                    <p className="subtitle">
                        <span className={`badge badge-${form?.status === 'published' ? 'success' : 'warning'} mr-2`}>
                            {form?.status || 'draft'}
                        </span>
                        Version {form?.currentVersion?.versionNumber || 1}
                        {hasChanges && <span style={{ color: '#f59e0b', marginLeft: 8 }}>● Unsaved changes</span>}
                    </p>
                </div>
                <div className="flex gap-3">
                    <button className="btn btn-ghost" onClick={() => setShowVersions(!showVersions)}>
                        <Clock size={16} /> Versions
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowPreview(!showPreview)}>
                        <Eye size={16} /> Preview
                    </button>
                    <button className="btn btn-secondary" onClick={handleSave} disabled={saving || !hasChanges}>
                        <Save size={16} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button className="btn btn-primary" onClick={handlePublish}>
                        <Globe size={16} /> Publish
                    </button>
                </div>
            </div>

            {/* Public URL bar */}
            {form?.publicToken && (
                <div className="card mb-4" style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Share2 size={16} color="#10b981" />
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Public URL:</span>
                    <code style={{ fontSize: '13px', flex: 1, color: '#6366f1' }}>
                        {window.location.origin}/apply/{form.publicToken}
                    </code>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/apply/${form.publicToken}`);
                        toast.success('Copied!');
                    }}>
                        <Copy size={14} /> Copy
                    </button>
                </div>
            )}

            {/* Version History Panel */}
            {showVersions && (
                <div className="card mb-4">
                    <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: '14px' }}>Version History</h3>
                    {versions.length === 0 ? (
                        <p style={{ color: '#6b7280', fontSize: '13px' }}>No versions saved yet</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {versions.map((v: any) => (
                                <div key={v.id} className="flex justify-between items-center"
                                    style={{
                                        padding: '8px 12px', background: v.id === form?.currentVersionId ? 'rgba(99, 102, 241, 0.08)' : 'var(--color-bg-tertiary)',
                                        borderRadius: 8, fontSize: '13px',
                                        border: v.id === form?.currentVersionId ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid transparent',
                                    }}>
                                    <div>
                                        <span style={{ fontWeight: 600 }}>v{v.versionNumber}</span>
                                        <span style={{ color: '#6b7280', marginLeft: 8 }}>
                                            {new Date(v.createdAt).toLocaleString()}
                                        </span>
                                        {v.publishedAt && <span className="badge badge-success ml-2" style={{ fontSize: '10px' }}>Published</span>}
                                    </div>
                                    {v.id !== form?.currentVersionId && (
                                        <button className="btn btn-ghost btn-sm" onClick={() => {
                                            if (v.schema?.sections) {
                                                setSections(v.schema.sections);
                                                setHasChanges(true);
                                                toast.success(`Loaded version ${v.versionNumber}`);
                                            }
                                        }}>
                                            <RotateCcw size={14} /> Restore
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Builder Layout */}
            <div className="form-builder">
                <div className="form-builder-canvas">
                    {showPreview ? (
                        <FormPreview sections={sections} />
                    ) : (
                        <>
                            {sections.map((section, si) => (
                                <div key={section.id} className="card mb-6">
                                    {/* Section Header */}
                                    <div className="flex justify-between items-center mb-4">
                                        <div style={{ flex: 1 }}>
                                            <input
                                                value={section.title}
                                                onChange={(e) => updateSection(si, { title: e.target.value })}
                                                style={{
                                                    background: 'transparent', border: 'none', fontSize: '1.1rem',
                                                    fontWeight: 700, color: 'var(--color-accent)', outline: 'none', width: '100%',
                                                }}
                                            />
                                            <input
                                                value={section.description}
                                                onChange={(e) => updateSection(si, { description: e.target.value })}
                                                placeholder="Section description..."
                                                style={{
                                                    background: 'transparent', border: 'none', fontSize: '13px',
                                                    color: '#6b7280', outline: 'none', width: '100%', marginTop: 4,
                                                }}
                                            />
                                        </div>
                                        <button className="btn btn-ghost btn-sm" onClick={() => removeSection(si)}
                                            style={{ color: '#ef4444' }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>

                                    {/* Fields */}
                                    {section.fields.map((field: any, fi: number) => (
                                        <div key={field.id}
                                            className="field-block"
                                            onClick={() => setActiveField({ sectionId: section.id, fieldId: field.id })}
                                            style={{
                                                cursor: 'pointer',
                                                border: activeField?.fieldId === field.id ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid transparent',
                                                background: activeField?.fieldId === field.id ? 'rgba(99, 102, 241, 0.04)' : undefined,
                                            }}>
                                            <div className="flex items-center gap-3">
                                                <div className="flex flex-col" style={{ gap: 0 }}>
                                                    <button className="btn btn-ghost" onClick={(e) => { e.stopPropagation(); moveField(section.id, fi, 'up'); }}
                                                        style={{ padding: 1, opacity: fi === 0 ? 0.2 : 0.6 }} disabled={fi === 0}>
                                                        <ChevronUp size={12} />
                                                    </button>
                                                    <button className="btn btn-ghost" onClick={(e) => { e.stopPropagation(); moveField(section.id, fi, 'down'); }}
                                                        style={{ padding: 1, opacity: fi === section.fields.length - 1 ? 0.2 : 0.6 }}
                                                        disabled={fi === section.fields.length - 1}>
                                                        <ChevronDown size={12} />
                                                    </button>
                                                </div>
                                                <span style={{ fontSize: '14px' }}>
                                                    {FIELD_TYPES.find(ft => ft.type === field.type)?.icon}
                                                </span>
                                                <input
                                                    value={field.label}
                                                    onChange={(e) => { e.stopPropagation(); updateField(section.id, field.id, { label: e.target.value }); }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        background: 'transparent', border: 'none', fontSize: '14px',
                                                        fontWeight: 500, color: 'var(--color-text-primary)', outline: 'none', flex: 1,
                                                    }}
                                                />
                                                <span style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    {FIELD_TYPES.find(ft => ft.type === field.type)?.label}
                                                </span>
                                                {field.required && (
                                                    <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: 600 }}>Required</span>
                                                )}
                                                <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); removeField(section.id, field.id); }}
                                                    style={{ padding: 4, color: '#ef4444' }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add Field Buttons */}
                                    <div className="flex gap-2 mt-4" style={{ flexWrap: 'wrap' }}>
                                        {FIELD_TYPES.slice(0, 6).map(ft => (
                                            <button key={ft.type} className="btn btn-ghost btn-sm"
                                                onClick={() => addField(section.id, ft.type)}>
                                                {ft.icon} {ft.label}
                                            </button>
                                        ))}
                                        <details style={{ position: 'relative' }}>
                                            <summary className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', listStyle: 'none' }}>
                                                + More Fields
                                            </summary>
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                                                background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
                                                borderRadius: 10, padding: 8, zIndex: 10, minWidth: 200,
                                                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                                            }}>
                                                {FIELD_TYPES.slice(6).map(ft => (
                                                    <button key={ft.type} className="btn btn-ghost btn-sm"
                                                        style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 2 }}
                                                        onClick={() => addField(section.id, ft.type)}>
                                                        {ft.icon} {ft.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </details>
                                    </div>
                                </div>
                            ))}

                            <button className="btn btn-secondary w-full" onClick={addSection}>
                                <Plus size={16} /> Add Section
                            </button>
                        </>
                    )}
                </div>

                {/* Right Panel — Field Settings */}
                <div className="form-builder-palette">
                    {activeFieldData ? (
                        <FieldSettings
                            field={activeFieldData}
                            sectionId={activeField!.sectionId}
                            onUpdate={(updates: any) => updateField(activeField!.sectionId, activeField!.fieldId, updates)}
                            onClose={() => setActiveField(null)}
                        />
                    ) : (
                        <div>
                            <h3 style={{ marginBottom: 12, fontWeight: 700, fontSize: '14px' }}>Field Types</h3>
                            <p style={{ color: '#6b7280', fontSize: '12px', marginBottom: 12 }}>
                                Click a field type or click "Add" buttons in sections to add fields
                            </p>
                            {['basic', 'choice', 'advanced'].map(group => (
                                <div key={group} style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontWeight: 600 }}>
                                        {group}
                                    </div>
                                    {FIELD_TYPES.filter(ft => ft.group === group).map(ft => (
                                        <div key={ft.type} className="field-block" style={{ cursor: 'pointer', marginBottom: 6 }}
                                            onClick={() => sections.length > 0 && addField(sections[sections.length - 1].id, ft.type)}>
                                            <span style={{ marginRight: 8 }}>{ft.icon}</span>
                                            <span style={{ fontSize: '13px', fontWeight: 500 }}>{ft.label}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Field Settings Panel ─────────────────────────────────────

function FieldSettings({ field, sectionId, onUpdate, onClose }: {
    field: any; sectionId: string; onUpdate: (updates: any) => void; onClose: () => void;
}) {
    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 style={{ fontWeight: 700, fontSize: '14px' }}>Field Settings</h3>
                <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
            </div>

            <div className="form-group">
                <label className="form-label" style={{ fontSize: '12px' }}>Label</label>
                <input className="form-input" value={field.label}
                    onChange={(e) => onUpdate({ label: e.target.value })} />
            </div>

            <div className="form-group">
                <label className="form-label" style={{ fontSize: '12px' }}>Placeholder</label>
                <input className="form-input" value={field.placeholder || ''}
                    onChange={(e) => onUpdate({ placeholder: e.target.value })}
                    placeholder="Hint text..." />
            </div>

            <div className="form-group">
                <label className="form-label" style={{ fontSize: '12px' }}>Description</label>
                <textarea className="form-textarea" value={field.description || ''}
                    onChange={(e) => onUpdate({ description: e.target.value })}
                    placeholder="Helper text / disclaimer details..." rows={3}
                    style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={field.required || false}
                        onChange={(e) => onUpdate({ required: e.target.checked })} />
                    Required field
                </label>
            </div>

            {/* Select / Radio / Checkbox options */}
            {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                <div className="form-group">
                    <label className="form-label" style={{ fontSize: '12px' }}>Options</label>
                    {(field.options || []).map((opt: string, i: number) => (
                        <div key={i} className="flex gap-2 mb-2">
                            <input className="form-input" value={opt}
                                onChange={(e) => {
                                    const opts = [...(field.options || [])];
                                    opts[i] = e.target.value;
                                    onUpdate({ options: opts });
                                }}
                                style={{ flex: 1 }} />
                            <button className="btn btn-ghost btn-sm" onClick={() => {
                                const opts = (field.options || []).filter((_: any, j: number) => j !== i);
                                onUpdate({ options: opts });
                            }} style={{ color: '#ef4444', padding: 4 }}><X size={14} /></button>
                        </div>
                    ))}
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                        onUpdate({ options: [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`] });
                    }}><Plus size={14} /> Add Option</button>
                </div>
            )}

            {/* Table settings */}
            {field.type === 'table' && (
                <>
                    <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>Table Columns</label>
                        {(field.columns || []).map((col: string, i: number) => (
                            <div key={i} className="flex gap-2 mb-2">
                                <input className="form-input" value={col}
                                    onChange={(e) => {
                                        const cols = [...(field.columns || [])];
                                        cols[i] = e.target.value;
                                        onUpdate({ columns: cols });
                                    }} style={{ flex: 1 }} />
                                <button className="btn btn-ghost btn-sm" onClick={() => {
                                    const cols = (field.columns || []).filter((_: any, j: number) => j !== i);
                                    onUpdate({ columns: cols });
                                }} style={{ color: '#ef4444', padding: 4 }}><X size={14} /></button>
                            </div>
                        ))}
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                            onUpdate({ columns: [...(field.columns || []), `Column ${(field.columns?.length || 0) + 1}`] });
                        }}><Plus size={14} /> Add Column</button>
                    </div>
                    <div className="flex gap-3">
                        <div className="form-group" style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Min Rows</label>
                            <input type="number" className="form-input" value={field.minRows || 1}
                                onChange={(e) => onUpdate({ minRows: Number(e.target.value) })} min={1} />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Max Rows</label>
                            <input type="number" className="form-input" value={field.maxRows || 10}
                                onChange={(e) => onUpdate({ maxRows: Number(e.target.value) })} min={1} />
                        </div>
                    </div>
                </>
            )}

            {/* Repeatable settings */}
            {field.type === 'repeatable' && (
                <>
                    <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>Template Fields</label>
                        {(field.templateFields || []).map((tf: any, i: number) => (
                            <div key={tf.id} className="flex gap-2 mb-2 items-center">
                                <select className="form-select" value={tf.type} onChange={(e) => {
                                    const tfs = [...(field.templateFields || [])];
                                    tfs[i] = { ...tfs[i], type: e.target.value };
                                    onUpdate({ templateFields: tfs });
                                }} style={{ width: 100, fontSize: '12px' }}>
                                    <option value="text">Text</option>
                                    <option value="email">Email</option>
                                    <option value="number">Number</option>
                                    <option value="date">Date</option>
                                </select>
                                <input className="form-input" value={tf.label}
                                    onChange={(e) => {
                                        const tfs = [...(field.templateFields || [])];
                                        tfs[i] = { ...tfs[i], label: e.target.value };
                                        onUpdate({ templateFields: tfs });
                                    }} style={{ flex: 1 }} />
                                <button className="btn btn-ghost btn-sm" onClick={() => {
                                    onUpdate({ templateFields: (field.templateFields || []).filter((_: any, j: number) => j !== i) });
                                }} style={{ color: '#ef4444', padding: 4 }}><X size={14} /></button>
                            </div>
                        ))}
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                            onUpdate({
                                templateFields: [...(field.templateFields || []), {
                                    id: String(Date.now()), type: 'text', label: 'Field', required: false,
                                }],
                            });
                        }}><Plus size={14} /> Add Template Field</button>
                    </div>
                    <div className="flex gap-3">
                        <div className="form-group" style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Min Entries</label>
                            <input type="number" className="form-input" value={field.minEntries || 1}
                                onChange={(e) => onUpdate({ minEntries: Number(e.target.value) })} min={0} />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Max Entries</label>
                            <input type="number" className="form-input" value={field.maxEntries || 10}
                                onChange={(e) => onUpdate({ maxEntries: Number(e.target.value) })} min={1} />
                        </div>
                    </div>
                </>
            )}

            {/* File upload settings */}
            {field.type === 'file' && (
                <>
                    <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>Accepted File Types</label>
                        <input className="form-input" value={field.acceptedTypes || ''}
                            onChange={(e) => onUpdate({ acceptedTypes: e.target.value })}
                            placeholder=".pdf,.doc,.docx,.png,.jpg" />
                    </div>
                    <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>Max File Size (MB)</label>
                        <input type="number" className="form-input" value={field.maxSizeMB || 10}
                            onChange={(e) => onUpdate({ maxSizeMB: Number(e.target.value) })} min={1} />
                    </div>
                </>
            )}
        </div>
    );
}

// ── Form Preview ─────────────────────────────────────────────

function FormPreview({ sections }: { sections: any[] }) {
    return (
        <div>
            <div style={{
                padding: '20px 24px', background: 'rgba(99, 102, 241, 0.05)',
                borderRadius: 12, marginBottom: 20, border: '1px solid rgba(99, 102, 241, 0.1)',
            }}>
                <div className="flex items-center gap-2" style={{ fontSize: '13px', color: '#6366f1' }}>
                    <Eye size={16} /> Preview Mode — This is how candidates will see the form
                </div>
            </div>

            {sections.map((section) => (
                <div key={section.id} className="card mb-6">
                    <h3 style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4, color: 'var(--color-accent)' }}>
                        {section.title}
                    </h3>
                    {section.description && (
                        <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: 16 }}>{section.description}</p>
                    )}

                    {section.fields.map((field: any) => (
                        <div key={field.id} className="form-group">
                            <label className="form-label">
                                {field.label}
                                {field.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                            </label>
                            {field.description && (
                                <p style={{ fontSize: '12px', color: '#4b5563', margin: '4px 0 8px', fontStyle: 'italic', lineHeight: 1.5 }}>
                                    {field.description}
                                </p>
                            )}

                            {(field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'number' || field.type === 'date') && (
                                <input className="form-input" type={field.type === 'phone' ? 'tel' : field.type}
                                    placeholder={field.placeholder || ''} disabled />
                            )}

                            {field.type === 'textarea' && (
                                <textarea className="form-textarea" placeholder={field.placeholder || ''} rows={3} disabled />
                            )}

                            {field.type === 'select' && (
                                <select className="form-select" disabled>
                                    <option>{field.placeholder || 'Select an option...'}</option>
                                    {(field.options || []).map((opt: string, i: number) => (
                                        <option key={i}>{opt}</option>
                                    ))}
                                </select>
                            )}

                            {field.type === 'radio' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {(field.options || []).map((opt: string, i: number) => (
                                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', cursor: 'pointer' }}>
                                            <input type="radio" name={field.id} disabled /> {opt}
                                        </label>
                                    ))}
                                </div>
                            )}

                            {field.type === 'checkbox' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {(field.options || []).map((opt: string, i: number) => (
                                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', cursor: 'pointer' }}>
                                            <input type="checkbox" disabled /> {opt}
                                        </label>
                                    ))}
                                </div>
                            )}

                            {field.type === 'file' && (
                                <div style={{
                                    padding: 20, border: '2px dashed var(--color-border)', borderRadius: 10,
                                    textAlign: 'center', color: '#6b7280', fontSize: '13px',
                                }}>
                                    📎 Drop file here or click to upload
                                    {field.acceptedTypes && <div style={{ fontSize: '11px', marginTop: 4 }}>Accepted: {field.acceptedTypes}</div>}
                                    {field.maxSizeMB && <div style={{ fontSize: '11px' }}>Max size: {field.maxSizeMB}MB</div>}
                                </div>
                            )}

                            {field.type === 'table' && (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            {(field.columns || []).map((col: string, i: number) => (
                                                <th key={i}>{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: field.minRows || 1 }).map((_, ri) => (
                                            <tr key={ri}>
                                                {(field.columns || []).map((_c: string, ci: number) => (
                                                    <td key={ci}><input className="form-input" disabled style={{ background: 'transparent', border: 'none' }} /></td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {field.type === 'repeatable' && (
                                <div style={{ padding: 16, background: 'var(--color-bg-tertiary)', borderRadius: 10, border: '1px dashed var(--color-border)' }}>
                                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: 8 }}>🔄 Repeatable Section — Candidates can add {field.minEntries || 1}–{field.maxEntries || 10} entries</p>
                                    {(field.templateFields || []).map((tf: any) => (
                                        <div key={tf.id} className="form-group" style={{ marginBottom: 8 }}>
                                            <label className="form-label" style={{ fontSize: '12px' }}>{tf.label}</label>
                                            <input className="form-input" type={tf.type} disabled />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
